'use client'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { IconEye, IconEyeClosed } from '@tabler/icons-react'
import Cookies from 'js-cookie'
import { useRouter } from 'next/navigation'
import { getSession, signIn } from 'next-auth/react'

import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import Button from 'shared/ui/Button'
import {
  ControlledTextField
} from 'shared/ui/form/ControlledElements/ControlledTextField'
import IconButton from 'shared/ui/IconButton'
import Link from 'shared/ui/Link/Link'
import toast from 'shared/ui/utils/toast'
import { PATHS } from 'shared/utils/paths'
import { LocalStorage } from 'shared/utils/storage'
import { emailRegex } from 'shared/utils/validators'

import { userLoginSeo } from '../analytics'
import { resendActivationLink } from '../api/auth'
import {
  LOGIN_REDIRECT_LOCAL_STORAGE_KEY,
  UNAUTHENTICATED_CART_ID_LOCAL_STORAGE_KEY
} from '../utils'

import GoogleLoginButton from './GoogleLoginButton'

import styles from './LoginForm.module.scss'

const defaultValues = {
  email: '',
  password: ''
}

interface ValuesTypes {
  email: string
  password: string
}

interface LoginFormProps {
  onModalClose?: () => void
}

const LoginForm = ({ onModalClose }: LoginFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const router = useRouter()

  const methods = useForm<ValuesTypes>({
    mode: 'onChange',
    defaultValues
  })

  const { handleSubmit } = methods

  const onSubmit = async (data: ValuesTypes) => {
    setIsLoading(true)

    LocalStorage.keys().forEach(key => {
      if (
        ![
          UNAUTHENTICATED_CART_ID_LOCAL_STORAGE_KEY,
          LOGIN_REDIRECT_LOCAL_STORAGE_KEY
        ].includes(key)
      ) {
        new LocalStorage(key).remove()
      }
    })

    const response = await signIn('credentials', {
      email: data.email.toLowerCase(),
      password: data.password,
      redirect: false
    })

    if (response && response.error) {
      let message = response.error?.replace(/,/g, '\n')

      if (response.error === 'CredentialsSignin') {
        const authErrorCookie = Cookies.get('auth_error')

        if (authErrorCookie) {
          message = decodeURIComponent(authErrorCookie)

          Cookies.remove('auth_error')
        }
      }

      if (message.includes('Konto nie zostało aktywowane')) {
        toast.error(
          <div className={styles.resendActivationLinkWrapper}>
            <div>{message}</div>
            <Button
              onClick={async () => {
                resendActivationLink({ email: data.email })
                  .then(() => {
                    toast.success('Email z linkiem aktywacyjnym został wysłany')
                  })
                  .catch(() => {
                    toast.error(
                      'Nie udało się wysłać maila z linkiem aktywacyjnym'
                    )
                  })
              }}
              variant='primary'
              size='small'
            >
              Wyślij link aktywacyjny
            </Button>
          </div>
        )
      } else {
        toast.error(message)
      }

      setIsLoading(false)

      return
    } else {
      const session = await getSession()
      const customer = session?.user?.customer

      userLoginSeo({ user: customer, method: 'credentials' })
      syneriseSendFormData({
        formData: {
          email: customer?.email || ''
        },
        event: SyneriseFormEvent.LOGIN,
        fieldMapping: {
          email: 'email'
        }
      })

      const localStorageRedirectLink = new LocalStorage(
        LOGIN_REDIRECT_LOCAL_STORAGE_KEY
      )
      const redirectLink = localStorageRedirectLink.get<string | null>(null)

      if (redirectLink) {
        router.replace(redirectLink)
        localStorageRedirectLink.remove()
      }
      setIsLoading(false)
    }

    if (onModalClose) {
      onModalClose()
    }
  }

  const handlePasswordVisibilityChange = () => {
    setIsPasswordVisible(prev => !prev)
  }

  return (
    <FormProvider {...methods}>
      <form noValidate className={styles.formWrapper} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formHeader}>Zaloguj się</div>
        <ControlledTextField
          id='login_email'
          name='email'
          label='Email'
          required
          rules={{
            required: 'Email jest wymagany',
            pattern: {
              value: emailRegex,
              message: 'Niepoprawny adres email'
            },
            setValueAs: (value: string) => value.trim()
          }}
          size='large'
        />
        <ControlledTextField
          id='login_password'
          name='password'
          type={isPasswordVisible ? 'text' : 'password'}
          label='Hasło'
          required
          rules={{
            required: 'Hasło jest wymagane'
          }}
          endIcon={
            <IconButton
              onClick={handlePasswordVisibilityChange}
              variant='transparent'
              size='medium'
              type='button'
              aria-label={isPasswordVisible ? 'Ukryj hasło' : 'Pokaż hasło'}
            >
              {isPasswordVisible ? <IconEyeClosed /> : <IconEye />}
            </IconButton>
          }
        />
        <div className={styles.forgotPassword}>
          <Link href={PATHS.forgotPassword}>Odzyskaj hasło</Link>
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            id='login_submit_button'
            fullWidth
            variant='primaryOutline'
            size='large'
            type='submit'
            disabled={isLoading}
          >
            Zaloguj się
          </Button>
          <div className={styles.divider} />
          <GoogleLoginButton />

          <div className={styles.noAccount}>
            Nie masz konta? <Link href={PATHS.register}>Zarejestruj się</Link>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default LoginForm
